import { SelectItem } from "@fronterahealth/frontera-ui-components";

import { MoveGoalBankGoalInput, useMoveTargetToFolderMutation } from "@api/graphql/types-and-hooks";
import { FolderManagementAction } from "@components/GoalsBank/constants";
import { FolderManagementDialog } from "@components/GoalsBank/dialogs/FolderManagementDialog";
import { useGetShortTermGoals } from "@components/GoalsBank/hooks/useGetShortTermGoals";
import { findFolderSelection } from "@components/GoalsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifySuccess } from "@components/notifications/notifications";

interface MoveTargetToFolderProps {
  currentFolderId: string;
  goalBankTargetId?: string;
  onCloseClick: () => void;
  open: boolean;
  foldersSelection: SelectItem[];
  refetch: () => void;
}

export const MoveTargetGoalToFolder: React.FC<MoveTargetToFolderProps> = ({
  currentFolderId,
  goalBankTargetId,
  onCloseClick,
  open,
  foldersSelection,
  refetch,
}) => {
  const moveTarget = useMoveTargetToFolderMutation({});
  const { formState, RegisteredFormSelected, reset, onSubmit, watch } = useFormUtils<MoveGoalBankGoalInput>({
    mutationFn: async (params) => {
      if (params.newFolderId && goalBankTargetId)
        moveTarget.mutateAsync(
          {
            input: {
              goalBankTargetId,
              goalBankShortTermId: params.goalBankShortTermId,
              currentFolderId,
              newFolderId: params.newFolderId,
            },
          },
          {
            onSuccess: async () => {
              await refetch();
              notifySuccess(`Successfully Moved Target`);
              reset();
              onCloseClick();
            },
            onError: async (error: unknown) => {
              console.error(`Error when moving target `, error);
            },
          },
        );
    },
  });
  const newFolderIdWatch = watch("newFolderId");
  const { shortTermGoals } = useGetShortTermGoals(newFolderIdWatch ?? currentFolderId);
  const placeholderText = () => {
    const selection = findFolderSelection(newFolderIdWatch ?? currentFolderId, foldersSelection);
    return selection ? selection.primary : "Select a folder";
  };

  return (
    <FolderManagementDialog
      action={FolderManagementAction.MOVE}
      open={open}
      onCancelClick={onCloseClick}
      onSaveClick={onSubmit}
    >
      <FormContainer onSubmit={onSubmit}>
        <RegisteredFormSelected
          formKey="newFolderId"
          required
          formState={formState}
          items={foldersSelection}
          title={"Select Folder"}
          placeholderText={placeholderText()}
        />
        <RegisteredFormSelected
          required={true}
          formKey="goalBankShortTermId"
          formState={formState}
          items={
            (shortTermGoals?.getGoalBankGoals?.edges.map((edge) => {
              return {
                id: edge?.node?.id,
                primary: edge?.node?.goalName,
                className: "ph-no-capture",
              };
            }) as SelectItem[]) || []
          }
          title="Short Term Goal"
          placeholderText={"Select Short Term Goal"}
        />
      </FormContainer>
    </FolderManagementDialog>
  );
};
